import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { FiBell } from 'react-icons/fi';
import { useSSENotifications } from '../hooks/useSSENotifications';
import { API_URL } from '../config/api';

const NotificationBell = () => {
  const [unreadCount, setUnreadCount] = useState(0);
  const navigate = useNavigate();
  const transientFailuresRef = useRef(0);

  // 権限レベルを取得（ゲスト=1、準会員=2、正会員=3+）
  const getPermissionLevel = () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return 1;
      const parts = token.split('.');
      if (parts.length === 3) {
        const base64Url = parts[1];
        const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
        const jsonPayload = decodeURIComponent(
          window
            .atob(base64)
            .split('')
            .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
            .join('')
        );
        const tokenData = JSON.parse(jsonPayload);
        return tokenData.permission_level || 1;
      }
    } catch (error) {
      console.error('JWT decode error:', error);
    }
    return 1;
  };

  const fetchUnreadCount = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;

      const permLevel = getPermissionLevel();
      if (permLevel < 3) return;

      const [notifResponse, chatResponse] = await Promise.all([
        axios.get(`${API_URL}/api/notifications/unread-count`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
        axios
          .get(`${API_URL}/api/chat/groups`, {
            headers: { Authorization: `Bearer ${token}` },
          })
          .catch(() => ({ data: { chats: [] } })),
      ]);

      const notifCount = notifResponse.data?.unread_count || 0;
      setUnreadCount(notifCount);
      transientFailuresRef.current = 0;
    } catch (error) {
      const status = error?.response?.status;
      const isTransient =
        error?.code === 'ERR_NETWORK' ||
        error?.message === 'Network Error' ||
        status === 503 ||
        status === 502 ||
        status === 504;
      if (isTransient) {
        transientFailuresRef.current += 1;
        if (transientFailuresRef.current === 1) {
          console.warn('未読件数取得をスキップ（接続一時障害）');
        }
      } else {
        console.error('未読件数取得エラー:', error);
      }
    }
  };

  const handleSSENotification = useCallback((data) => {
    console.log('🔔 SSE通知受信:', data);
    
    if (data.type === 'notification_update') {
      setUnreadCount(data.unreadCount || 0);
    } else if (data.type === 'new_notification') {
      setUnreadCount((prev) => prev + 1);
    } else if (data.type === 'notification_read') {
      fetchUnreadCount();
    }
  }, []);

  const sseConnected = useSSENotifications(handleSSENotification);

  useEffect(() => {
    const permLevel = getPermissionLevel();
    if (permLevel < 3) return;

    fetchUnreadCount();
    const interval = setInterval(fetchUnreadCount, 120000);

    const handleChatReadUpdated = () => {
      fetchUnreadCount();
    };
    
    const handleNotificationRead = () => {
      fetchUnreadCount();
    };
    
    const handleNewNotification = () => {
      fetchUnreadCount();
    };

    window.addEventListener('chat-read-updated', handleChatReadUpdated);
    window.addEventListener('notification-read', handleNotificationRead);
    window.addEventListener('cartUpdated', handleNewNotification);
    window.addEventListener('walletBalanceUpdate', handleNewNotification);

    return () => {
      clearInterval(interval);
      window.removeEventListener('chat-read-updated', handleChatReadUpdated);
      window.removeEventListener('notification-read', handleNotificationRead);
      window.removeEventListener('cartUpdated', handleNewNotification);
      window.removeEventListener('walletBalanceUpdate', handleNewNotification);
    };
  }, []);


  return (
    <button
      className="relative p-2 text-white hover:bg-white/20 rounded-full transition-colors focus:outline-none"
      onClick={() => navigate('/notifications')}
    >
      <FiBell size={20} />
      {unreadCount > 0 && (
        <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full min-w-5 h-5 px-1 flex items-center justify-center font-bold animate-pulse">
          {unreadCount > 99 ? '99+' : unreadCount}
        </span>
      )}
    </button>
  );
};

export default NotificationBell;
